import { useState, useEffect } from 'react';
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Alert,
  Snackbar,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Fab,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Archive as ArchiveIcon } from '@mui/icons-material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import UserButton from './components/UserButton';
import LandingPage from './components/LandingPage';
import VoiceRecorder from './components/VoiceRecorder';
import EisenhowerMatrix from './components/EisenhowerMatrix';
import ManualTaskForm from './components/ManualTaskForm';
import { Task } from './config/n8n.config';
import n8nService, { setAuthToken, setCurrentUser } from './services/n8nService';
import useWebSocket from './hooks/useWebSocket';
import { WebSocketMessage } from './services/websocketService';

// Create MindMatrix theme with warm colors
const theme = createTheme({
  palette: {
    primary: {
      main: 'rgb(120, 113, 108)', // primary-500
      dark: 'rgb(68, 64, 60)',     // primary-700
      light: 'rgb(231, 218, 202)', // primary-300
    },
    secondary: {
      main: 'rgb(249, 115, 22)',   // accent-500
      dark: 'rgb(194, 65, 12)',    // accent-700
      light: 'rgb(253, 186, 116)', // accent-300
    },
    background: {
      default: 'rgb(254, 252, 248)', // primary-50
      paper: 'rgb(255, 255, 255)',
    },
    text: {
      primary: 'rgb(28, 25, 23)',     // primary-900
      secondary: 'rgb(87, 83, 81)',   // primary-600
    },
  },
  typography: {
    // Define a responsive font size scale
    h1: { fontSize: '2.5rem', '@media (max-width:600px)': { fontSize: '2rem' } },
    h2: { fontSize: '2rem', '@media (max-width:600px)': { fontSize: '1.75rem' } },
    h3: { fontSize: '1.75rem', '@media (max-width:600px)': { fontSize: '1.5rem' } },
    h4: { fontSize: '1.5rem', '@media (max-width:600px)': { fontSize: '1.25rem' } },
    h5: { fontSize: '1.25rem', '@media (max-width:600px)': { fontSize: '1.1rem' } },
    h6: { fontSize: '1.1rem', '@media (max-width:600px)': { fontSize: '1rem' } },
    body1: { fontSize: '1rem', '@media (max-width:600px)': { fontSize: '0.9rem' } },
    body2: { fontSize: '0.875rem', '@media (max-width:600px)': { fontSize: '0.8rem' } },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          touchAction: 'manipulation',
          overscrollBehavior: 'none',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          overflowX: 'hidden',
          background: 'linear-gradient(to bottom right, rgb(254, 252, 248), rgb(251, 247, 240))',
        },
        '*': {
          scrollbarWidth: 'thin',
        },
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgb(168, 162, 158)',
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgb(120, 113, 108)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, rgb(249, 115, 22), rgb(234, 88, 12))',
          boxShadow: '0 4px 6px rgba(249, 115, 22, 0.1), 0 2px 4px rgba(0, 0, 0, 0.02)',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, rgb(249, 115, 22), rgb(234, 88, 12))',
          boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, rgb(234, 88, 12), rgb(194, 65, 12))',
            boxShadow: '0 6px 16px rgba(249, 115, 22, 0.4)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          background: 'linear-gradient(135deg, rgb(249, 115, 22), rgb(234, 88, 12))',
          boxShadow: '0 2px 8px rgba(249, 115, 22, 0.2)',
          '&:hover': {
            background: 'linear-gradient(135deg, rgb(234, 88, 12), rgb(194, 65, 12))',
            boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
            transform: 'translateY(-1px)',
          },
        },
        outlinedPrimary: {
          borderColor: 'rgb(249, 115, 22)',
          color: 'rgb(249, 115, 22)',
          '&:hover': {
            borderColor: 'rgb(234, 88, 12)',
            backgroundColor: 'rgba(249, 115, 22, 0.04)',
          },
        },
      },
    },
    MuiButtonBase: {
      // Disable ripple effect on touch devices for better performance
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '@media (hover: hover)': {
            // Enable ripple only on devices that support hover
            disableRipple: false,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        // Apply performance optimizations for animations
        root: {
          willChange: 'transform',
          transform: 'translateZ(0)',
        },
      },
    },
    MuiCollapse: {
      styleOverrides: {
        // Reduce animation duration on smaller screens
        root: {
          transitionDuration: '300ms',
          '@media (max-width:600px)': {
            transitionDuration: '150ms',
          },
        },
      },
    },
  },
  transitions: {
    // Standardize transition durations
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
});

function MainApp() {
  const { currentUser, session } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [archivedTasks, setArchivedTasks] = useState<Task[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [n8nConnected, setN8nConnected] = useState(false);
  const [manualTaskFormOpen, setManualTaskFormOpen] = useState(false);

  const isSignedIn = !!currentUser;
  console.log('🔍 App render - currentUser:', currentUser ? 'Present' : 'None');
  console.log('🔍 App render - session:', session ? 'Present' : 'None');
  console.log('🔍 App render - isSignedIn:', isSignedIn);

  // WebSocket connection for real-time updates
  const { isConnected: wsConnected } = useWebSocket({
    onMessage: (message: WebSocketMessage) => {
      console.log('Received WebSocket message:', message);
      handleWebSocketMessage(message);
    },
    onConnect: () => {
      console.log('WebSocket connected');
    },
    onDisconnect: () => {
      console.log('WebSocket disconnected');
    },
    autoConnect: true // Enable auto-connection for real-time updates
  });

  // Suppress unused variable warning for wsConnected (used for connection status)
  console.debug('WebSocket connection status:', wsConnected);

  // Set up auth token and load data when component mounts
  useEffect(() => {
    const setupAuthAndLoadData = async () => {
      // Only proceed if user is signed in
      if (!isSignedIn) {
        console.log('User not signed in, skipping backend calls');
        setAuthToken(null);
        setCurrentUser(null);
        setN8nConnected(false);
        return;
      }

      try {
        const token = session?.access_token;
        console.log('Got Supabase token, setting up backend connection');
        setAuthToken(token || null);
        setCurrentUser(currentUser); // Set current user for token refresh

        // Now that auth is set up, load data
        await checkN8nConnection();
        await loadTasks();
        await loadArchivedTasks();
      } catch (error) {
        console.error('Error setting up auth or loading data:', error);
        setError('Failed to initialize application. Please try refreshing the page.');
      }
    };
    
    setupAuthAndLoadData();

    return () => {
      n8nService.disconnectSSE();
    };
  }, [currentUser, session, isSignedIn]);

  // Setup SSE when backend connection is established (disabled for now)
  useEffect(() => {
    // SSE disabled for Express backend - using REST API polling instead
    // if (n8nConnected) {
    //   setupSSE();
    // }
  }, [n8nConnected]);


  const loadTasks = async () => {
    try {
      // Load active (non-archived) tasks
      const fetchedTasks = await n8nService.getTasks(false);
      setTasks(fetchedTasks);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load tasks';
      setError(errorMessage);
    }
  };

  const loadArchivedTasks = async () => {
    try {
      const fetchedArchivedTasks = await n8nService.getTasks(true);
      setArchivedTasks(fetchedArchivedTasks);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load archived tasks';
      setError(errorMessage);
    }
  };

  const checkN8nConnection = async () => {
    try {
      const isConnected = await n8nService.checkN8nHealth();
      setN8nConnected(isConnected);
    } catch (err) {
      setN8nConnected(false);
    }
  };

  // Handle WebSocket messages for real-time updates
  const handleWebSocketMessage = (message: WebSocketMessage) => {
    console.log('🔍 Processing WebSocket message:', message.type, message.data);
    switch (message.type) {
      case 'task-created':
        console.log('🔍 Adding task to state:', message.data);
        setTasks(prev => {
          console.log('🔍 Current tasks before adding:', prev.length);
          const newTasks = [...prev, message.data];
          console.log('🔍 New tasks after adding:', newTasks.length);
          return newTasks;
        });
        setSuccessMessage('New task created!');
        break;

      case 'task-updated':
        setTasks(prev => prev.map(task =>
          task.id === message.data.id ? message.data : task
        ));
        setSuccessMessage('Task updated!');
        break;

      case 'task-deleted':
        setTasks(prev => prev.filter(task => task.id !== message.data.id));
        setSuccessMessage('Task deleted!');
        break;

      case 'task-moved':
        setTasks(prev => prev.map(task =>
          task.id === message.data.id ? message.data : task
        ));
        setSuccessMessage('Task moved!');
        break;

      case 'task-completed':
        // Task completed and auto-archived
        setTasks(prev => prev.filter(task => task.id !== message.data.id));
        setArchivedTasks(prev => {
          const existingIndex = prev.findIndex(task => task.id === message.data.id);
          if (existingIndex >= 0) {
            // Update existing archived task
            const newTasks = [...prev];
            newTasks[existingIndex] = message.data;
            return newTasks;
          } else {
            // Add to archived tasks
            return [...prev, message.data];
          }
        });
        setSuccessMessage(`Task completed and archived: "${message.data.title}"! 🎉`);
        break;

      case 'task-archived':
        // Remove from active tasks and add to archived
        setTasks(prev => prev.filter(task => task.id !== message.data.id));
        setArchivedTasks(prev => {
          const existingIndex = prev.findIndex(task => task.id === message.data.id);
          if (existingIndex >= 0) {
            // Update existing archived task
            const newTasks = [...prev];
            newTasks[existingIndex] = message.data;
            return newTasks;
          } else {
            // Add to archived tasks
            return [...prev, message.data];
          }
        });
        setSuccessMessage('Task archived!');
        break;

      case 'task-unarchived':
        // Remove from archived tasks and add to active
        setArchivedTasks(prev => prev.filter(task => task.id !== message.data.id));
        setTasks(prev => {
          const existingIndex = prev.findIndex(task => task.id === message.data.id);
          if (existingIndex >= 0) {
            // Update existing task
            const newTasks = [...prev];
            newTasks[existingIndex] = message.data;
            return newTasks;
          } else {
            // Add to active tasks
            return [...prev, message.data];
          }
        });
        setSuccessMessage('Task unarchived!');
        break;

      case 'transcription-complete':
        setSuccessMessage(`Transcription complete! Found ${message.data.extractedTasks?.length || 0} tasks.`);
        // Don't call loadTasks() here - tasks are already added via WebSocket
        break;

      case 'subscribed':
        console.log('Successfully subscribed to WebSocket updates');
        break;

      default:
        console.log('Unknown WebSocket message type:', message.type);
    }
  };

  // const setupSSE = () => {
  //   // SSE disabled for Express backend - using REST API instead
  //   console.log('SSE setup disabled - using REST API polling instead');
  //   return;

  //   // Only connect to SSE if backend is available
  //   if (n8nConnected) {
  //     // Connect to SSE for real-time updates
  //     n8nService.connectToSSE();

  //     // Listen for all SSE events
  //     n8nService.onSSEEvent('*', handleSSEEvent);
  //   }
  // }; // Removed as it is unused

  // const handleSSEEvent = (event: SSEEvent) => {
  //   switch (event.type) {
  //     case 'task-created':
  //       setTasks(prev => [...prev, event.data]);
  //       setSuccessMessage('New task created from voice note!');
  //       break;

  //     case 'task-updated':
  //       setTasks(prev => prev.map(task =>
  //         task.id === event.data.id ? event.data : task
  //       ));
  //       break;

  //     case 'task-deleted':
  //       setTasks(prev => prev.filter(task => task.id !== event.data.id));
  //       break;

  //     case 'task-moved':
  //       setTasks(prev => prev.map(task =>
  //         task.id === event.data.id ? event.data : task
  //       ));
  //       break;

  //     case 'transcription-complete':
  //       setSuccessMessage('Voice transcription completed!');
  //       // Reload tasks to get any newly created tasks
  //       loadTasks();
  //       break;

  //     default:
  //       console.log('Unknown SSE event:', event);
  //   }
  // }; // Commented out since SSE is disabled

  const handleTranscriptionComplete = async (_transcription: string, extractedTasks: any[]) => {
    setSuccessMessage(`Transcription complete! Found ${extractedTasks.length} tasks.`);
    // Since WebSocket is disabled on Vercel, reload tasks to get the newly created ones
    await loadTasks();
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleTasksChange = (updatedTasks: Task[]) => {
    setTasks(updatedTasks);
  };

  const handleCloseError = () => {
    setError('');
  };

  const handleCloseSuccess = () => {
    setSuccessMessage('');
  };

  const handleOpenManualTaskForm = () => {
    console.log('🔵 ADD TASK button clicked - opening manual task form');
    setManualTaskFormOpen(true);
  };

  const handleCloseManualTaskForm = () => {
    console.log('🔴 Closing manual task form');
    setManualTaskFormOpen(false);
  };

  const handleTaskCreated = async (newTask: Task) => {
    console.log('🟢 New task created:', newTask);
    // Since WebSocket is disabled on Vercel, reload tasks to show the new task immediately
    setSuccessMessage(`Task "${newTask.title}" created successfully!`);
    await loadTasks();
  };

  if (!isSignedIn) {
    console.log('🏠 Rendering LandingPage - user not signed in');
    return <LandingPage />;
  }

  console.log('🏢 Rendering main app - user is signed in');

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1 }}>
        {/* AppBar for signed-in users */}
        <AppBar position="static">
          <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                flexGrow: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              MindMatrix
            </Typography>
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<AddIcon />}
                onClick={handleOpenManualTaskForm}
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  display: { xs: 'none', sm: 'flex' }
                }}
              >
                Add Task
              </Button>
              <Typography
                variant="body2"
                sx={{ display: { xs: 'none', sm: 'block' } }}
              >
                Backend: {n8nConnected ? '🟢 Connected' : '🔴 Disconnected'}
              </Typography>
              <Box
                sx={{
                  display: { xs: 'block', sm: 'none' },
                  fontSize: '1.2rem'
                }}
              >
                {n8nConnected ? '🟢' : '🔴'}
              </Box>
              <UserButton />
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main content for signed-in users */}
        <Container
          maxWidth={false}
          sx={{
            px: { xs: 1, sm: 2, md: 3 },
            mt: { xs: 1, sm: 2, md: 4 },
            mb: { xs: 8, sm: 4, md: 4 }
          }}
        >
          {!n8nConnected && (
            <Alert
              severity="warning"
              sx={{
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Backend is not connected. Make sure the Express server is running on http://localhost:5000.
            </Alert>
          )}
          
          {/* Archive Toggle Controls */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showArchived}
                  onChange={(e) => {
                    setShowArchived(e.target.checked);
                    if (e.target.checked && archivedTasks.length === 0) {
                      loadArchivedTasks();
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArchiveIcon />
                  <Typography variant="body2">
                    Show Archived Tasks ({showArchived ? archivedTasks.length : tasks.length} tasks)
                  </Typography>
                </Box>
              }
            />
          </Box>
          
          {!showArchived && (
            <VoiceRecorder
              onTranscriptionComplete={handleTranscriptionComplete}
              onError={handleError}
            />
          )}
          
          <EisenhowerMatrix
            tasks={showArchived ? archivedTasks : tasks}
            onTasksChange={showArchived ? setArchivedTasks : handleTasksChange}
            onError={handleError}
            showArchived={showArchived}
            onArchiveToggle={(taskId: string, archived: boolean) => {
              if (archived) {
                // Archive task
                n8nService.archiveTask(taskId).catch(err => {
                  console.error('Failed to archive task:', err);
                  setError('Failed to archive task');
                });
              } else {
                // Unarchive task
                n8nService.unarchiveTask(taskId).catch(err => {
                  console.error('Failed to unarchive task:', err);
                  setError('Failed to unarchive task');
                });
              }
            }}
          />
        </Container>
      </Box>

      {/* FAB for signed-in users */}
      <Fab
        color="primary"
        aria-label="add task"
        onClick={handleOpenManualTaskForm}
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: 1000,
          width: { xs: 48, sm: 56 },
          height: { xs: 48, sm: 56 }
        }}
      >
        <AddIcon />
      </Fab>

      {/* Manual Task Form for signed-in users */}
      <ManualTaskForm
        open={manualTaskFormOpen}
        onClose={handleCloseManualTaskForm}
        onTaskCreated={handleTaskCreated}
      />

      {/* Snackbars for notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}

export default App;
